import RegularShape from 'ol/style/RegularShape';
import { asString } from 'ol/color';
import Circle from 'ol/geom/Circle';
import MultiPoint from 'ol/geom/MultiPoint';
import Polygon from 'ol/geom/Polygon';
import { Icon } from 'ol/style';
import Fill from 'ol/style/Fill';
import Style from 'ol/style/Style';
import { buffer, extend } from 'ol/extent';
import CircleStyle from 'ol/style/Circle';

import { Stroke } from 'cadenza/map/style/stroke';
import { SketchFeatureProperty } from 'cadenza/map/classic-sketch-layer/sketch-feature-property';
import { pixelBuffer } from 'cadenza/map/style/style-util';
import { Rectangle } from 'cadenza/map/geom/rectangle';
import { getOvalRenderer } from 'cadenza/map/geom/oval-renderer';
import { Oval } from 'cadenza/map/geom/oval';
import { GeometryType } from 'cadenza/map/geom/geometry-type';

const WHITE = [ 255, 255, 255, 1 ];
const TRANSPARENT_WHITE = [ 255, 255, 255, 0.25 ];
const BLUE = [ 0, 153, 255, 1 ];
const BLUE_RGB = asString(BLUE);
const BLUE_STROKE = new Stroke({ color: BLUE, width: 1 });
const BLUE_WIDER_STROKE = new Stroke({ color: BLUE, width: 2 });
export const WHITE_FILL = new Fill({ color: WHITE });

/*
 * The creation of some of the OL styles depends on the Canvas API.
 * To avoid hitting the Canvas API when the module is simply loaded,
 * which creates problems in unit tests for example,
 * we create the styles lazily, only when really needed.
 */
let geometryStyles;

/**
 * @param {import('cadenza/map/geom/geometry-type').GeometryType} geometryType
 * @return {import('ol/style').StyleLike}
 */
export function getStyleForGeometryType (geometryType) {
  if (!geometryStyles) {
    geometryStyles = createStyles();
  }
  return geometryStyles[geometryType];
}

export function getGeometryType (feature) {
  const geometry = feature.getGeometry();

  // Oval geometry does return only 'LineString' or 'Circle' as type. We need to override this and
  // return the 'Oval' here so that we get the correct style (which is incompatible with the others.
  // See Oval.getType() for the background of this special behavior.
  if (geometry instanceof Oval) {
    return GeometryType.OVAL;
  }

  return geometry.getType();
}

export function getSelectStyle (feature, resolution) {
  if (!feature || !feature.getGeometry()) {
    return null;
  }
  const style = getStyleForGeometryType(getGeometryType(feature));
  const styles = (typeof style === 'function') ? style(feature) : style;
  const groupFeatures = feature.get(SketchFeatureProperty.GROUP_FEATURES);
  if (groupFeatures) {
    let groupExtent;
    for (const groupFeature of groupFeatures) {
      const groupFeatureStyle = getStyleForGeometryType(getGeometryType(feature));
      const groupFeatureStyles = (typeof groupFeatureStyle === 'function') ? groupFeatureStyle(groupFeature) : groupFeatureStyle;
      const pixelBuffers = groupFeatureStyles.map(featureSelectStyle => pixelBuffer(featureSelectStyle, groupFeature));
      const groupFeatureExtent = [ ...groupFeature.getGeometry().getExtent() ];
      for (const pB of pixelBuffers) {
        let bufferedStyleExtent = [ ...groupFeature.getGeometry().getExtent() ];
        if (pB.length === 4) {
          for (let i = 0; i < pB.length; i++) {
            bufferedStyleExtent[i] += (pB[i] * resolution);
          }
        } else {
          bufferedStyleExtent = buffer(bufferedStyleExtent, resolution * pB);
        }
        extend(groupFeatureExtent, bufferedStyleExtent);
      }
      if (!groupExtent) {
        groupExtent = groupFeatureExtent;
      } else {
        extend(groupExtent, groupFeatureExtent);
      }
    }
    return styles.concat(getFeatureGroupBoxStyle(groupExtent));
  }
  return styles;
}

function getFeatureGroupBoxStyle (extent) {
  return new Style({
    stroke: new Stroke({ color: BLUE, lineDash: [ 3, 6 ] }),
    fill: new Fill({ color: TRANSPARENT_WHITE }),
    geometry: new Polygon([ [
      [ extent[0], extent[1] ],
      [ extent[2], extent[1] ],
      [ extent[2], extent[3] ],
      [ extent[0], extent[3] ],
      [ extent[0], extent[1] ]
    ] ])
  });
}

export function createModifyPointStyle () {
  return new Style({
    image: createSquareStyle(6, BLUE_STROKE, WHITE_FILL)
  });
}

function createStyles () {
  const styles = {};

  styles[GeometryType.POLYGON] = [
    new Style({ fill: new Fill({ color: TRANSPARENT_WHITE }) })
  ];

  styles[GeometryType.LINE_STRING] = [
    new Style({ stroke: BLUE_WIDER_STROKE }),
    new Style({
      image: createSquareStyle(4, BLUE_STROKE, WHITE_FILL),
      zIndex: Infinity,
      geometry: getGeometryVertices
    })
  ];

  styles[GeometryType.POINT] = feature => {
    const featureStyleOrGetter = feature.getStyle();
    const featureStyle = featureStyleOrGetter instanceof Function ? featureStyleOrGetter() : featureStyleOrGetter;
    const featureStyles = [
      new Style({
        image: new CircleStyle({
          radius: 5,
          stroke: BLUE_WIDER_STROKE,
          fill: WHITE_FILL
        })
      })
    ];

    if (featureStyle) {
      const imageStyle = featureStyle.getImage();
      const textStyle = featureStyle.getText();

      if (textStyle) {
        featureStyles.push(createTextFrameStyle(textStyle, BLUE_STROKE));
      } else if (imageStyle instanceof Icon) {
        featureStyles.push(createIconFrameStyle(imageStyle, BLUE_RGB));
      }
    }

    return featureStyles;
  };

  styles[GeometryType.POLYGON] = styles[GeometryType.POLYGON].concat(styles[GeometryType.LINE_STRING]);
  styles[GeometryType.LINE] = styles[GeometryType.LINE_STRING];
  styles[GeometryType.RECTANGLE] = styles[GeometryType.POLYGON];
  styles[GeometryType.CIRCLE] = styles[GeometryType.POLYGON];
  styles[GeometryType.OVAL] = styles[GeometryType.POLYGON].map(style =>
    style.getImage() ? style : new Style({ renderer: getOvalRenderer(style) }));

  styles[GeometryType.MULTI_POLYGON] = styles[GeometryType.POLYGON];
  styles[GeometryType.MULTI_LINE_STRING] = styles[GeometryType.LINE_STRING];
  styles[GeometryType.MULTI_POINT] = styles[GeometryType.POINT];

  return styles;
}

function createSquareStyle (radius, stroke, fill) {
  return new RegularShape({
    points: 4,
    radius,
    angle: Math.PI / 4,
    stroke,
    fill
  });
}

function getGeometryVertices (feature) {
  const geometry = feature.getGeometry();
  let vertices;
  if (geometry instanceof Oval) {
    vertices = [ geometry.getCenter(), ...geometry.getCorners() ];
  } else if (geometry instanceof Rectangle) {
    vertices = geometry.getVertices();
  } else if (geometry.getType() === GeometryType.POLYGON) {
    vertices = geometry.getCoordinates().flat();
  } else if (geometry.getType() === GeometryType.MULTI_POLYGON) {
    vertices = geometry.getCoordinates().flatMap(g => g.flat());
  } else if (geometry.getType() === GeometryType.MULTI_LINE_STRING) {
    vertices = geometry.getCoordinates().flat();
  } else if (geometry instanceof Circle) {
    vertices = [ geometry.getCenter() ];
  } else {
    vertices = geometry.getCoordinates();
  }
  return new MultiPoint(vertices);
}

function createTextFrameStyle (originalTextStyle, stroke, padding = 3) {
  const textSelectionStyle = originalTextStyle.clone();
  textSelectionStyle.setBackgroundStroke(stroke);
  textSelectionStyle.setPadding(originalTextStyle.getPadding().map(x => x + padding));
  return new Style({
    text: textSelectionStyle
  });
}

function createIconFrameStyle (originalImageStyle, color, padding = 3) {
  const [ width, height ] = originalImageStyle.getImageSize().map(x => x + padding * 2);
  const rotation = originalImageStyle.getRotation();
  const style = new Style({
    renderer: ([ x, y ], { context, pixelRatio }) => {
      const scaledWidth = width * pixelRatio;
      const scaledHeight = height * pixelRatio;
      const x1 = Math.round(x - scaledWidth / 2);
      const y1 = Math.round(y - scaledHeight / 2);

      context.save();
      context.translate(x, y);
      context.rotate(rotation);
      context.translate(-x, -y);
      context.strokeStyle = color;
      context.strokeRect(x1, y1, scaledWidth, scaledHeight);
      context.restore();
    }
  });
  style.renderProperties = { width, height, rotation };
  return style;
}
